import React, { useState, ReactNode } from 'react';
import { Tabs, Tab, Box } from '@mui/material';
import styles from './BasicTabs.module.scss';

interface TabsProps {
  tabs: string[];
  tabContents: ReactNode[];
  initialTab?: number;
  activeTab?: number;
  tabsChangeSideEffect?: () => void;
  /**
   * Makes the tabs controlled: the selected tab is `activeTab` and a click only calls this
   * callback (e.g. to store the tab in the URL). Without it the tabs keep their own state.
   */
  onTabChange?: (tab: number) => void;
}

export default function BasicTabs({
  tabs,
  tabContents,
  initialTab,
  activeTab,
  tabsChangeSideEffect,
  onTabChange,
}: TabsProps) {
  const [internalValue, setInternalValue] = useState<number>(initialTab ?? activeTab ?? 0);
  const isControlled = Boolean(onTabChange);
  const requestedValue = isControlled ? (activeTab ?? 0) : internalValue;
  // Guard against an out-of-range index coming e.g. from a hand-edited URL.
  const value = requestedValue >= 0 && requestedValue < tabContents.length ? requestedValue : 0;

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    if (onTabChange) {
      onTabChange(newValue);
    } else {
      setInternalValue(newValue);
    }
    if (tabsChangeSideEffect) {
      tabsChangeSideEffect();
    }
  };

  return (
    <>
      <Tabs
        variant='scrollable'
        scrollButtons='auto'
        allowScrollButtonsMobile
        value={value}
        onChange={handleChange}
        aria-label='dynamic tabs'
        className={styles.tab_margin}
      >
        {tabs.map((tab) => (
          <Tab
            id={`basicTabsTab_${tab}`}
            key={tab}
            label={tab}
            className={styles.tab_border_radius}
          />
        ))}
      </Tabs>

      <Box>{tabContents[value]}</Box>
    </>
  );
}
